<template>
  <div :class="fullView ? 'full-view' : 'contracts-ongoing-closed-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { COLOR_PALETTE, getDefaultChartTextStyle, getBarNoDataOptions } from '@/helpers/insightsCharts'
import { useChartResize } from '@/composables/useChartResize'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, LegendComponent, BarChart, SVGRenderer])

export default {
  name: 'ContractsOngoingClosedChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()

    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        title: {
          text: this.$t('insights.charts.contractsOngoingClosed.title'),
          show: true,
          top: 0,
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'item',
          formatter: function (params) {
            return `${params.marker} ${params.seriesName}: <strong>${params.value}</strong>`
          }
        },
        legend: {
          bottom: '0',
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView),
          selectedMode: false
        },
        color: COLOR_PALETTE,
        xAxis: {
          type: 'category',
          data: [''],
          show: false
        },
        yAxis: {
          type: 'value',
          show: false
        },
        grid: {
          left: 0,
          right: 0,
          top: 40,
          bottom: 40,
          containLabel: false
        },
        series: [
          {
            name: this.$t('insights.charts.contractsOngoingClosed.closed'),
            type: 'bar',
            stack: 'total',
            data: [],
            barWidth: '20%',
            label: {
              show: true,
              position: 'inside',
              ...getDefaultChartTextStyle(this.fullView),
              formatter: '{c}'
            }
          },
          {
            name: this.$t('insights.charts.contractsOngoingClosed.ongoing'),
            type: 'bar',
            stack: 'total',
            data: [],
            barWidth: '20%',
            label: {
              show: true,
              position: 'inside',
              ...getDefaultChartTextStyle(this.fullView),
              formatter: '{c}'
            }
          }
        ]
      }
    }
  },
  mounted () {
    this.updateData()
    window.addEventListener('resize', this.handleResize)
  },
  methods: {
    updateData () {
      const noDataOptions = getBarNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = {
          ...this.option,
          ...noDataOptions,
          title: this.option.title
        }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.tablePivot()
      const activeRow = pivot.find(row => row['contracts.active'] === true)
      const inactiveRow = pivot.find(row => row['contracts.active'] === false)

      const activeContracts = activeRow ? Number(activeRow['contracts.count']) : 0
      const inactiveContracts = inactiveRow ? Number(inactiveRow['contracts.count']) : 0

      this.option.series[0].data = [inactiveContracts]
      this.option.series[1].data = [activeContracts]
      emitChartFinished(this, false)
    }
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.contracts-ongoing-closed-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
